// src/components/MesasExamen/modales/ModalPreviasMesa.jsx
import React, { useEffect, useState } from "react";
import { FaTimes, FaExchangeAlt, FaTrash } from "react-icons/fa";
import BASE_URL from "../../../config/config";
import ModalMoverPreviaMesa from "./ModalMoverPreviaMesa";
import "./ModalPreviasMesa.css";

/**
 * ModalPreviasMesa
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - numeroMesa: number
 * - onPreviaMoved: function(mensaje, json) => void
 * - onPreviaDeleted: function(mensaje, json) => void
 */
const ModalPreviasMesa = ({
  open,
  onClose,
  numeroMesa,
  onPreviaMoved,
  onPreviaDeleted,
}) => {
  const [loading, setLoading] = useState(false);
  const [previas, setPrevias] = useState([]);
  const [error, setError] = useState(null);

  const [reloadFlag, setReloadFlag] = useState(0);

  const [selectedPrevia, setSelectedPrevia] = useState(null);

  const [previaEliminar, setPreviaEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  useEffect(() => {
    if (!open || !numeroMesa) return;

    let abortado = false;

    const cargarPrevias = async () => {
      setLoading(true);
      setError(null);

      try {
        const resp = await fetch(
          `${BASE_URL}/api.php?action=mesas_previas_por_mesa`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ numero_mesa: Number(numeroMesa) }),
          }
        );

        const json = await resp.json().catch(() => ({}));

        if (!resp.ok || !json?.exito) {
          throw new Error(json?.mensaje || `HTTP ${resp.status}`);
        }

        if (!abortado) {
          setPrevias(Array.isArray(json.data) ? json.data : []);
        }
      } catch (e) {
        if (!abortado) {
          setError(e.message || "Error al obtener las previas de la mesa.");
        }
      } finally {
        if (!abortado) setLoading(false);
      }
    };

    cargarPrevias();

    return () => {
      abortado = true;
    };
  }, [open, numeroMesa, reloadFlag]);

  useEffect(() => {
    if (!open) {
      setPrevias([]);
      setError(null);
      setSelectedPrevia(null);
      setPreviaEliminar(null);
      setEliminando(false);
    }
  }, [open]);

  if (!open) return null;

  const handleOverlayClick = () => {
    if (selectedPrevia || previaEliminar) return;
    if (!loading && !eliminando) onClose?.();
  };

  const stop = (e) => e.stopPropagation();

  const handleAbrirMoverPrevia = (previa) => {
    if (eliminando) return;
    setSelectedPrevia(previa);
  };

  const handleCierreMoverPrevia = () => {
    setSelectedPrevia(null);
  };

  const handleAbrirEliminarPrevia = (previa) => {
    if (eliminando) return;
    setPreviaEliminar(previa);
    setError(null);
  };

  const handleCerrarEliminarPrevia = () => {
    if (eliminando) return;
    setPreviaEliminar(null);
  };

  const handlePreviaMovida = (json) => {
    setSelectedPrevia(null);

    if (typeof onPreviaMoved === "function") {
      const msg =
        json?.mensaje || "Previa movida correctamente a la nueva mesa.";
      onPreviaMoved(msg, json);
    }

    onClose?.();
  };

  const handleConfirmarEliminarPrevia = async () => {
    if (!previaEliminar?.id_previa || !numeroMesa) {
      setError("No se pudo identificar la previa o la mesa.");
      return;
    }

    setEliminando(true);
    setError(null);

    try {
      const resp = await fetch(
        `${BASE_URL}/api.php?action=mesas_eliminar_previa_mesa`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            numero_mesa: Number(numeroMesa),
            id_previa: Number(previaEliminar.id_previa),
          }),
        }
      );

      const json = await resp.json().catch(() => ({}));

      if (!resp.ok || !json?.exito) {
        throw new Error(json?.mensaje || `HTTP ${resp.status}`);
      }

      const idEliminado = Number(previaEliminar.id_previa);

      setPrevias((actuales) =>
        actuales.filter((p) => Number(p.id_previa) !== idEliminado)
      );

      setPreviaEliminar(null);

      if (typeof onPreviaDeleted === "function") {
        const msg =
          json?.mensaje || "Alumno eliminado de la mesa correctamente.";
        onPreviaDeleted(msg, json);
      } else if (typeof onPreviaMoved === "function") {
        onPreviaMoved(
          json?.mensaje || "Alumno eliminado de la mesa correctamente.",
          json
        );
      }

      setReloadFlag((f) => f + 1);
    } catch (e) {
      setError(e.message || "Error al eliminar el alumno de la mesa.");
    } finally {
      setEliminando(false);
    }
  };

  const cursoEliminar = previaEliminar
    ? `${previaEliminar.nombre_curso || previaEliminar.materia_id_curso || ""} ${
        previaEliminar.nombre_division ||
        previaEliminar.materia_id_division ||
        ""
      }`.trim()
    : "";

  return (
    <>
      <div
        className="mprev_overlay"
        role="dialog"
        aria-modal="true"
        onClick={handleOverlayClick}
      >
        <div className="mprev_modal" onClick={stop}>
          <header className="mprev_header">
            <h3 className="mprev_title">Previas de la mesa Nº {numeroMesa}</h3>

            <button
              type="button"
              className="mprev_close"
              onClick={onClose}
              disabled={loading || eliminando}
              aria-label="Cerrar"
            >
              <FaTimes />
            </button>
          </header>

          <div className="mprev_body">
            {loading && <p className="mprev_info">Cargando previas…</p>}

            {error && !loading && <p className="mprev_error">{error}</p>}

            {!loading && !error && (
              <>
                {previas.length === 0 ? (
                  <p className="mprev_info">
                    No hay previas asignadas a esta mesa.
                  </p>
                ) : (
                  <div className="mprev_tablewrapper">
                    <table className="mprev_table">
                      <thead>
                        <tr>
                          <th>DNI</th>
                          <th>Alumno</th>
                          <th>Curso</th>
                          <th style={{ textAlign: "center" }}>Acción</th>
                        </tr>
                      </thead>

                      <tbody>
                        {previas.map((p) => {
                          const curso =
                            p.nombre_curso || p.materia_id_curso || "";
                          const division =
                            p.nombre_division || p.materia_id_division || "";
                          const cursoCompleto = `${curso} ${division}`.trim();

                          return (
                            <tr key={p.id_previa}>
                              <td>{p.dni}</td>
                              <td>{p.alumno}</td>
                              <td>{cursoCompleto}</td>

                              <td className="mprev_actions">
                                <button
                                  type="button"
                                  className="mprev_actionbtn"
                                  title="Mover esta previa a otra mesa de la misma materia"
                                  onClick={() => handleAbrirMoverPrevia(p)}
                                  disabled={eliminando}
                                >
                                  <FaExchangeAlt />
                                </button>

                                <button
                                  type="button"
                                  className="mprev_actionbtn mprev_actionbtn_delete"
                                  title="Eliminar esta previa de la mesa"
                                  onClick={() => handleAbrirEliminarPrevia(p)}
                                  disabled={eliminando}
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          <footer className="mprev_footer">
            <button
              type="button"
              className="mprev_btnclose"
              onClick={onClose}
              disabled={loading || eliminando}
            >
              Cerrar
            </button>
          </footer>
        </div>
      </div>

      <ModalMoverPreviaMesa
        open={!!selectedPrevia}
        previa={selectedPrevia}
        onClose={handleCierreMoverPrevia}
        onMoved={handlePreviaMovida}
      />

      {previaEliminar && (
        <div
          className="logout-modal-overlay mprev-delete-logout-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-eliminar-previa-title"
          onMouseDown={handleCerrarEliminarPrevia}
        >
          <div
            className="logout-modal-container logout-modal--danger mprev-delete-logout-container"
            onMouseDown={stop}
          >
            <div className="logout-modal__icon is-danger" aria-hidden="true">
              <FaTrash />
            </div>

            <h3
              id="confirm-eliminar-previa-title"
              className="logout-modal-title logout-modal-title--danger"
            >
              Confirmar acción
            </h3>

            <p className="logout-modal-text">
              ¿Eliminar a <strong>{previaEliminar.alumno}</strong> de la mesa Nº{" "}
              <strong>{numeroMesa}</strong>?
            </p>

            <div className="prev-modal-item mprev-delete-detail">
              Mesa Nº {numeroMesa}
              {previaEliminar.dni ? ` • DNI: ${previaEliminar.dni}` : ""}
              {cursoEliminar ? ` • Curso: ${cursoEliminar}` : ""}
            </div>

            <div className="logout-modal-buttons">
              <button
                type="button"
                className="logout-btn logout-btn--ghost"
                onClick={handleCerrarEliminarPrevia}
                disabled={eliminando}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="logout-btn logout-btn--solid-danger"
                onClick={handleConfirmarEliminarPrevia}
                disabled={eliminando}
                aria-disabled={eliminando}
              >
                {eliminando ? "Eliminando…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ModalPreviasMesa;